import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Drawer as MuiDrawer,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { globalTheme } from "../../styles";
import { useState } from "react";
import { DataType, ORDERS_SEARCH_OPTIONS_LS, ORDER_SHIPMENT_SEARCH_OPTIONS_LS, orderShipmentFields } from "../../constants";

export const AdvancedSettingDrawer = ({
  open,
  page,
  searchFilters,
  onSaveOrCancelFilters,
  onSaveAndApplyFilters,
}: {
  open: boolean;
  page: "ORDERS" | "SHIPMENTS";
  searchFilters?: any;
  onSaveOrCancelFilters: () => void;
  onSaveAndApplyFilters: ({ sortByField, sortType, filters }) => void;
}) => {
  const orderShipmentSearchSavedParams = JSON.parse(localStorage.getItem(ORDER_SHIPMENT_SEARCH_OPTIONS_LS));
  const { searchOptions: shipmentsSearchOptions } = orderShipmentSearchSavedParams || { searchOptions: undefined };

  const ordersSearchSavedParams = JSON.parse(localStorage.getItem(ORDERS_SEARCH_OPTIONS_LS)) || searchFilters;

  const { searchOptions: ordersSearchOptions } = ordersSearchSavedParams || { searchOptions: undefined };
  const searchOptions = page === "SHIPMENTS" ? shipmentsSearchOptions : ordersSearchOptions;

  const [sortByField, setSortByField] = useState<string>(searchOptions?.sortOptions?.field || "");
  const [sortType, setSortType] = useState<"asc" | "desc" | null>(searchOptions?.sortOptions?.direction || null);

  const handleSortByFieldChange = (event: SelectChangeEvent) => {
    setSortByField(event.target.value);
  };

  const handleSortTypeChange = (value) => {
    setSortType(value);
  };

  const [filters, setFilters] = useState<{ field: string; operator: string; value: string | string[] }[]>(
    searchOptions?.filters || [{ field: "", operator: "", value: "" }]
  );

  // add additional filter
  const addFilter = () => {
    setFilters([...filters, { field: "", operator: "", value: "" }]);
  };

  // remove existing filter
  const removeFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  // update value of field in a selected filter rule
  const handleFieldChange = (index: number, value: string) => {
    const newFilters = [...filters];
    newFilters[index].field = value;
    newFilters[index].operator = "";
    newFilters[index].value = "";
    setFilters(newFilters);
  };

  const handleOperatorChange = (index: number, value: string) => {
    const newFilters = [...filters];
    newFilters[index].operator = value;
    setFilters(newFilters);
  };

  // update filter value in a given rule
  const handleFilterValueChange = (index: number, value: string | string[]) => {
    const newFilters = [...filters];
    newFilters[index].value = value;
    setFilters(newFilters);
  };

  const handleFilterInputChange = (e, field, index) => {
    let newValue;
    const value = e.target.value;

    switch (field?.fieldDataType) {
      case DataType.NUMBER:
        newValue = parseInt(value, 10); // Convert input to integer
        break;
      case DataType.DOUBLE:
        newValue = parseFloat(value); // Convert input to floating point
        break;
      case DataType.DATE:
        newValue = new Date(value).toISOString(); // Convert input to ISO string
        break;
      default:
        newValue = value; // Use the string directly for other types
        break;
    }

    handleFilterValueChange(index, newValue);
  };

  const getCriteriaFieldType = (fieldDataType: DataType): string => {
    switch (fieldDataType) {
      case DataType.NUMBER:
      case DataType.DOUBLE:
        return "number";
      case DataType.DATE:
        return "datetime-local";
      default:
        return "text";
    }
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const PresetOptionsMenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
        padding: 0,
      },
    },
  };

  const handlePresetValueChange = (index: number, event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    const newFilters = [...filters];

    if (filters[index]?.operator === "eq") {
      newFilters[index].value = Array.isArray(value) ? value.join(",") : value;
    } else {
      newFilters[index].value = typeof value === "string" ? value.split(",") : value;
    }

    setFilters(newFilters);
  };

  return (
    <MuiDrawer anchor="right" open={open} onClose={() => onSaveOrCancelFilters()}>
      <Stack sx={{ width: 780, height: "100%" }}>
        {/* Header */}
        <Typography
          variant="h6"
          padding={2}
          sx={{
            borderBottom: `1px solid ${globalTheme.border}`,
          }}
        >
          Advanced settings
        </Typography>

        {/* Content */}
        <Stack spacing={3} padding={2} flex={1}>
          <Stack
            sx={{
              border: `1px solid ${globalTheme.border}`,
            }}
          >
            <Box
              paddingX={2}
              paddingY={1}
              sx={{
                backgroundColor: globalTheme.background,
              }}
            >
              <Typography variant="subtitle1">Tab info</Typography>
            </Box>
            <Box paddingX={2} paddingY={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} style={{ width: "50%" }}>
                  <Typography variant="body2">Sort by: </Typography>
                  <FormControl sx={{ m: 1, minWidth: 120, width: "80%" }} size="small">
                    <Select
                      labelId="sort-by-field-label-id"
                      id="sort-by-field-id"
                      defaultValue={sortByField}
                      value={sortByField}
                      onChange={handleSortByFieldChange}
                    >
                      {orderShipmentFields
                        .filter((field) => field.enableForSort)
                        .map((field) => (
                          <MenuItem value={field.fieldName} key={`${field.fieldName}-menu-item}`}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{field.businessFieldName}</div>
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} style={{ width: "50%" }}>
                  <Typography variant="body2">Sort type: </Typography>
                  <FormControl sx={{ m: 1, minWidth: 120, width: "80%" }} size="small">
                    <Select
                      labelId="sort-type-label-id"
                      id="sort-type-id"
                      value={sortType || ""}
                      onChange={(e) => handleSortTypeChange(e.target.value)}
                    >
                      <MenuItem value={"asc"}>asc</MenuItem>
                      <MenuItem value={"desc"}>desc</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </Box>
          </Stack>

          <Stack
            spacing={2}
            sx={{
              border: `1px solid ${globalTheme.border}`,
            }}
          >
            <Box
              paddingX={2}
              paddingY={1}
              sx={{
                backgroundColor: globalTheme.background,
              }}
            >
              <Typography variant="subtitle1">Criteria</Typography>
            </Box>
            <Stack paddingX={2} paddingY={1} spacing={2}>
              {filters.map((filter, index) => {
                const field = orderShipmentFields.find((f) => f.fieldName === filter.field);
                return (
                  <Stack key={index} direction="row" alignItems="center" spacing={1}>
                    <FormControl sx={{ m: 1, minWidth: 120, width: "80%" }} size="small">
                      <Select
                        labelId={`select-field-label-id-${index}`}
                        id={`select-field-id-${index}`}
                        value={filter.field}
                        onChange={(e) => handleFieldChange(index, e.target.value as string)}
                      >
                        {orderShipmentFields.map((field) => (
                          <MenuItem value={field.fieldName} key={`${field.fieldName}-menu-item`}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{field.businessFieldName}</div>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl sx={{ m: 1, minWidth: 120, width: "80%" }} size="small">
                      <Select
                        labelId={`select-operator-label-id-${index}`}
                        id={`select-operator-id-${index}`}
                        value={filter.operator}
                        onChange={(e) => handleOperatorChange(index, e.target.value as string)}
                        disabled={!field} // Disable if no field is selected
                      >
                        {field?.operatorsAllowed.map((operator) => (
                          <MenuItem value={operator} key={`${operator}-menu-item`}>
                            {operator}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {field?.presetValues ? (
                      <FormControl sx={{ m: 1, minWidth: 120, width: "80%" }} size="small">
                        <Select
                          labelId={`multi-select-preset-filter-label-id-${index}`}
                          id={`multi-select-preset-filter-id-${index}`}
                          multiple={filter.operator === "eq" ? false : true}
                          value={Array.isArray(filter.value) ? filter.value : filter.value ? [filter.value] : []}
                          onChange={(e) => handlePresetValueChange(index, e)}
                          renderValue={(selected) => {
                            return selected.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {(selected as string[]).map((value) => {
                                  const field = orderShipmentFields.find((f) => f.fieldName === filter.field);
                                  const presetIndex = field?.presetValues?.indexOf(value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={
                                        field?.fieldName === "flagType" ? (
                                          <Stack direction="row" alignItems="center" spacing={1}>
                                            {/* Render icon for flagType, or fallback to "No Flag" */}
                                            {field?.presetFlagIcon && presetIndex >= 0 && field.presetFlagIcon[presetIndex] ? (
                                              <>
                                                {field.presetFlagIcon[presetIndex]}
                                                <Typography variant="body2">{field.businessIconName[presetIndex]}</Typography>
                                              </>
                                            ) : (
                                              <Typography variant="body2">No Flag</Typography>
                                            )}
                                          </Stack>
                                        ) : (
                                          value
                                        )
                                      }
                                    />
                                  );
                                })}
                              </Box>
                            ) : (
                              ""
                            );
                          }}
                          MenuProps={PresetOptionsMenuProps}
                        >
                          {field?.presetValues?.map((presetValue, index) => {
                            return (
                              <MenuItem key={index} value={presetValue}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <>
                                    {/* For flagType, render the corresponding icon or fallback text */}
                                    {field?.fieldName === "flagType" &&
                                    field?.presetFlagIcon &&
                                    index >= 0 &&
                                    field.presetFlagIcon[index] ? (
                                      <>
                                        {field.presetFlagIcon[index]}
                                        <Typography variant="body2">{field.businessIconName[index]}</Typography>
                                      </>
                                    ) : (
                                      field?.fieldName === "flagType" && <Typography variant="body2">No Flag</Typography>
                                    )}

                                    {/* For other fields, render the preset value as text */}
                                    {field?.fieldName !== "flagType" && presetValue}
                                  </>
                                </Stack>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        hiddenLabel
                        id="filled-hidden-label-small"
                        defaultValue=""
                        variant="outlined"
                        size="small"
                        disabled={!field} // Disable if no field is selected
                        type={getCriteriaFieldType(field?.fieldDataType)}
                        InputLabelProps={{
                          shrink: true, // This ensures the label shrinks when the input type is 'datetime-local'
                        }}
                        onChange={(e) => handleFilterInputChange(e, field, index)}
                      />
                    )}

                    <IconButton onClick={() => removeFilter(index)} aria-label="delete">
                      <DeleteIcon sx={{ color: globalTheme.red }} />
                    </IconButton>
                  </Stack>
                );
              })}
              <Button
                onClick={addFilter}
                variant="contained"
                color="primary"
                sx={{
                  width: "fit-content",
                }}
              >
                Add criteria
              </Button>
            </Stack>
          </Stack>
        </Stack>

        {/* Footer */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          padding={2}
          borderTop={`1px solid ${globalTheme.border}`}
        >
          <Button variant="outlined" color="error" onClick={() => onSaveOrCancelFilters()}>
            Cancel
          </Button>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              onClick={() => {
                localStorage.setItem(
                  page === "SHIPMENTS" ? ORDER_SHIPMENT_SEARCH_OPTIONS_LS : ORDERS_SEARCH_OPTIONS_LS,
                  JSON.stringify({
                    searchOptions: { filters: filters, sortOptions: { direction: sortType, field: sortByField } },
                  })
                );
                onSaveOrCancelFilters();
              }}
            >
              Save filters
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                localStorage.setItem(
                  page === "SHIPMENTS" ? ORDER_SHIPMENT_SEARCH_OPTIONS_LS : ORDERS_SEARCH_OPTIONS_LS,
                  JSON.stringify({
                    searchOptions: { filters: filters, sortOptions: { direction: sortType, field: sortByField } },
                  })
                );
                setFilters(filters);
                onSaveAndApplyFilters({ sortByField, sortType, filters });
              }}
            >
              Save & Apply filters
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </MuiDrawer>
  );
};
